import React, { useState, useMemo } from 'react';

const ExamsList = ({ exams, onEdit, onStatusChange, onManageQuestions }) => {
  const [activeTab, setActiveTab] = useState('draft');

  // Categorize exams based on dates and status
  const categorizedExams = useMemo(() => {
    const now = new Date();
    const draft = [];
    const scheduled = [];
    const upcoming = [];
    const active = [];
    const completed = [];

    exams.forEach(exam => {
      // Separate draft exams
      if (exam.status === 'draft') {
        draft.push(exam);
        return;
      }

      const startDateTime = new Date(`${exam.startDate}T${exam.startTime}`);
      const endDateTime = new Date(`${exam.endDate}T${exam.endTime}`);

      if (exam.status === 'cancelled') {
        completed.push(exam);
      } else if (now < startDateTime) {
        // Exam hasn't started yet
        const daysUntilStart = Math.ceil((startDateTime - now) / (1000 * 60 * 60 * 24));
        if (daysUntilStart <= 7) {
          upcoming.push(exam);
        } else {
          scheduled.push(exam);
        }
      } else if (now >= startDateTime && now <= endDateTime) {
        // Exam is currently running
        active.push(exam);
      } else {
        // Exam has ended
        completed.push(exam);
      }
    });

    return { draft, scheduled, upcoming, active, completed, all: exams };
  }, [exams]);

  const currentExams = categorizedExams[activeTab] || [];

  if (exams.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h4 className="text-lg font-bold text-gray-900">Exams</h4>
        </div>
        <div className="text-center py-12">
          <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-600 font-medium">No exams created yet</p>
          <p className="text-gray-500 text-sm mt-2">Go to "Create Exam" tab to create your first exam</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('draft')}
            className={`px-6 py-4 font-semibold text-sm whitespace-nowrap transition ${
              activeTab === 'draft'
                ? 'border-b-2 border-gray-600 text-gray-600 bg-gray-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Draft
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
              {categorizedExams.draft.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-4 font-semibold text-sm whitespace-nowrap transition ${
              activeTab === 'all'
                ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            All Exams
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
              {categorizedExams.all.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`px-6 py-4 font-semibold text-sm whitespace-nowrap transition ${
              activeTab === 'scheduled'
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Scheduled
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
              {categorizedExams.scheduled.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-4 font-semibold text-sm whitespace-nowrap transition ${
              activeTab === 'upcoming'
                ? 'border-b-2 border-orange-600 text-orange-600 bg-orange-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Upcoming (7 days)
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
              {categorizedExams.upcoming.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-6 py-4 font-semibold text-sm whitespace-nowrap transition ${
              activeTab === 'active'
                ? 'border-b-2 border-green-600 text-green-600 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-2">
              Active Now
              {categorizedExams.active.length > 0 && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
            </span>
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
              {categorizedExams.active.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-6 py-4 font-semibold text-sm whitespace-nowrap transition ${
              activeTab === 'completed'
                ? 'border-b-2 border-gray-600 text-gray-600 bg-gray-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Completed
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
              {categorizedExams.completed.length}
            </span>
          </button>
        </div>
      </div>
      {/* Exam Cards */}
      {currentExams.length === 0 ? (
        <div className="text-center py-12">
          <svg className="h-12 w-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 text-sm">No exams in this category</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
          {currentExams.map((exam) => (
          <div key={exam.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition">
            {exam.bannerImage && (
              <div className="w-full">
                <img 
                  src={exam.bannerImage} 
                  alt={exam.name} 
                  className="w-full h-48 object-cover" 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div className="p-6">
              <div className="mb-3">
                <h5 className="text-lg font-bold text-gray-900 mb-2">{exam.name}</h5>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    exam.status === 'active' ? 'bg-green-100 text-green-800' :
                    exam.status === 'hold' ? 'bg-orange-100 text-orange-800' :
                    exam.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {exam.status.toUpperCase()}
                  </span>
                  {exam.visibility === 'public' && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1">
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      PUBLIC
                    </span>
                  )}
                  {exam.showResultImmediately && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      Instant Results
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <p className="text-xs text-gray-500">Start</p>
                  <p className="text-sm font-semibold text-gray-900">{exam.startDate}</p>
                  <p className="text-sm text-gray-700">{exam.startTime}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End</p>
                  <p className="text-sm font-semibold text-gray-900">{exam.endDate}</p>
                  <p className="text-sm text-gray-700">{exam.endTime}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Questions</p>
                  <p className="text-sm font-semibold text-gray-900">{exam.totalQuestions}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Marks</p>
                  <p className="text-sm font-semibold text-gray-900">{exam.totalMarks}</p>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => onEdit(exam)}
                  className="flex-1 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition flex items-center justify-center gap-1"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-xs font-medium">Edit</span>
                </button>
                {onManageQuestions && (
                  <button 
                    onClick={() => onManageQuestions(exam)}
                    className="flex-1 p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition flex items-center justify-center gap-1"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs font-medium">Questions</span>
                  </button>
                )}
              </div>
              
              {/* Status Control */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Update Status:</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => onStatusChange(exam.id, 'active')}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${
                      exam.status === 'active'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => onStatusChange(exam.id, 'hold')}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${
                      exam.status === 'hold'
                        ? 'bg-orange-600 text-white'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                  >
                    Hold
                  </button>
                  <button
                    onClick={() => onStatusChange(exam.id, 'cancelled')}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${
                      exam.status === 'cancelled'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExamsList;
